import { find, omit, pick } from 'lodash-es'
import { store } from 'state/_store'
import { _appendAlert, _removeAlert, _updateAlert, getAlerts } from 'state/Alerts'
import { getSoundEl, blinkTab } from './notification'

//singular would be better, but then it "conflicts" with Bootstrap's Alert ¯\_(ツ)_/¯
export default class Alerts {

	static get TIMEOUT() { return 8 }

	static get TYPES() {
		return Object.freeze({
			SUCCESS : 'success',
			DANGER  : 'danger',
			WARNING : 'warning',
			INFO    : 'info',
			ORDER   : 'order',
		})
	}

	static #find = id => store.getState() |> getAlerts |> find(#, { id })

	/**
	 * A "middleman" function to appendAlert() so we can create IDs and implement the (non)-permanent behavior
	 * @param type {string} One of {@link Alerts.TYPES}
	 * @param content {string|JSX.Element|object} Either a message to display (string or JSX), or something specific that the alert type will handle internally (at the Alerts component).
	 * @param options {object|AlertOptions} The actual instance or a POJO with the same props
	 * @return {Promise<number>} The alert ID
	 */
	static async #add(type, content, options) {
		options = options instanceof AlertOptions? options : new AlertOptions(options)
		options.perm ||= options.flash
		const id = store.getState().alerts.nextId
		if (!options.perm) {
			options.progress = options.maxProgress = Alerts.TIMEOUT
		}
		await store.dispatch(_appendAlert({ id, content, type, ...options.directValues }))
		if (!options.perm) {
			const countdown = window.setInterval(() => Alerts.progress(id, false), 1000)
			Alerts.startupProgress(id, false)
			window.setTimeout(() => {
				window.clearInterval(countdown)
				Alerts.remove(id)
			}, Alerts.TIMEOUT * 1000)
		}
		return id
	}

	/**
	 * Changes a subset of the alert fields.
	 * @param id {number}
	 * @param fields {string|{ [content]: string, [progress]: boolean|number }} Either the text or any of the fields
	 */
	static update(id, fields) {
		let allowedFields = typeof fields == 'string'? { content: fields } : pick(fields, ['content', 'progress'])
		if (allowedFields.progress) {
			allowedFields.progress = Alerts.#calculateProgress(id, allowedFields.progress)
		}
		store.dispatch(_updateAlert({ id, ...allowedFields }))
	}

	/**
	 * Intelligently sets the alert's progress field.
	 * @param id {number}
	 * @param value {boolean|number} Numbers are set directly; otherwise, false = "decrement" and true = "increment"
	 */
	static progress = (id, value = true) => Alerts.update(id, { progress: Alerts.#calculateProgress(id, value) })

	/**
	 * Once you create an alert which will need a progress bar, it's important to call this function so it starts
	 * from 0 to 1 without waiting for the first step to be completed.
	 * @param id {number}
	 * @param direction {boolean} Either true for up or false for down (like {@link progress})
	 * @returns {number}
	 */
	static startupProgress = (id, direction = true) => window.setTimeout(() => Alerts.progress(id, direction), 100)

	static #calculateProgress = (id, v) => Number.isFinite(v)? v : Alerts.#find(id)?.progress + (v? 1 : -1)

	/**
	 * Removes the alert from state and turns off any extra side effects of it.
	 * @param id {number} The alert ID you got when you created it
	 * @param wait {boolean} Removes the alert after 1sec - useful to let the progress bar fill before dropping it.
	 * @return {number}
	 */
	static remove(id, wait = false) {
		wait?
			window.setTimeout(() => Alerts.#internalRemove(id), 1000) :
			Alerts.#internalRemove(id)
	}

	static #internalRemove(id) {
		const alert = getAlerts(store.getState()).find(a => a.id == id)
		if (alert) { //may have been removed already by user action, while this is called by the alert's timer
			if (alert.type == Alerts.TYPES.ORDER) {
				getSoundEl().pause()
			}
			blinkTab(false)
			store.dispatch(_removeAlert(id))
		}
	}

	/*** methods for specific types allows us to control what can be issued - primary/sec/light/dark shouldn't be used ***/

	/**
	 * @param msg {string|JSX.Element}
	 * @param options {object|AlertOptions}
	 * @return {void|Promise<number>} The alert ID - "optionally void" as we rarely need to await for this response
	 */
	static success = (msg, options = {}) => Alerts.#add(Alerts.TYPES.SUCCESS, msg, options)

	/**
	 * @param msg {string|JSX.Element}
	 * @param options {object|AlertOptions}
	 * @return {void|Promise<number>} The alert ID - "optionally void" as we rarely need to await for this response
	 * @alias {@link error}
	 */
	static danger = (msg, options = {}) => Alerts.#add(Alerts.TYPES.DANGER, msg, options)

	/** @see This is an alias of {@link danger} */
	static error = Alerts.danger

	/**
	 * @param msg {string|JSX.Element}
	 * @param options {object|AlertOptions}
	 * @return {void|Promise<number>} The alert ID - "optionally void" as we rarely need to await for this response
	 */
	static warning = (msg, options = {}) => Alerts.#add(Alerts.TYPES.WARNING, msg, options)

	/**
	 * @param msg {string|JSX.Element}
	 * @param options {object|AlertOptions}
	 * @return {void|Promise<number>} The alert ID - "optionally void" as we rarely need to await for this response
	 */
	static info = (msg, options = {}) => Alerts.#add(Alerts.TYPES.INFO, msg, options)

	/**
	 * @param order {Order|{ id, number, customer_name }} If the order ID is Alert.FAKE_LINK, it will generate a no-op link that
	 * will just dismiss the alert
	 * @param flash {boolean} Do we need a flashing alert, or just an informational one?
	 * @return {void|Promise<number>} The alert ID - "optionally void" as we rarely need to await for this response
	 */
	static order = (order, flash = false) => Alerts.#add(Alerts.TYPES.ORDER, pick(order, ['id', 'number', 'customer_name']), { flash })
}

class AlertOptions {
	/**
	 *
	 * @type string
	 */ link = ''

	/** If it's a permanent alert or auto-dismisses after {@link Alerts.TIMEOUT}
	 * @type boolean
	 *
	 */ perm = false

	/**
	 * @type boolean
	 */ flash = false

	/** Just used for custom progress alerts. Otherwise, respects "perm"
	 * @type number
	 */ progress = 0

	/** Just used for custom progress alerts. Otherwise, respects "perm"
	 * @type number
	 */ maxProgress

	constructor(props) { Object.assign(this, props) }

	get directValues() { return omit(this, 'perm') }
}
